// src/components/StageSummaryView.tsx
// Colorful, per-stage read-only renderer for StageDataModal — the sibling of
// PreConsultSummaryView.tsx for the other nine stages in the visit pipeline
// (Verify, Register, Vitals, Queue, Consultation, IP, Pharmacy, Billing,
// Resolve). Each stage has a small, hand-written "known fields" renderer
// (colored chips for anything with an inherent status/priority meaning,
// plain rows otherwise) plus a generic "Additional details" fallback for any
// key the known renderer doesn't explicitly consume — so a stage config that
// grows a new field (e.g. Register's dynamic intake_form_config) never goes
// missing here, it just shows up unstyled until someone teaches this file
// about it.
import { useEffect, useState, type ReactNode } from 'react';
import { ValueChip, type ValueChipTone } from './ui/ValueChip';
import { StatTile } from './ui/StatTile';
import { VitalTile } from './ui/VitalTile';
import { checkBp, checkPulse, checkTemp, checkSpo2, checkBmi } from '../lib/vitalThresholds';
import { humanize, isEmptyValue, RenderValue } from '../utils/stageDataRender';
import { listAllergies, listVisitDiagnoses, listPrescriptionsForVisit, getPrescriptionVersioned } from '../features/consultation/clinicalApi';
import type { Allergy, EncounterDiagnosis, Prescription } from '../features/consultation/types';
import DocumentEditor from '../features/ai-assist/DocumentEditor';
import DocumentPreviewButtons from './DocumentPreviewButtons';
import { WhyTodaySymptoms } from '../features/symptom-form/WhyTodaySymptoms';
import { readSnapshot } from '../features/symptom-form/symptomForm';

/** stage_data is a JSONB blob — its exact shape varies per stage, per
 * template, and per era of the app, so every view below reads it loosely by
 * design (matching the legacy .jsx this file replaces) rather than modeling
 * one strict interface per stage that would immediately drift. */
export type StageData = Record<string, any>;

/** The minimal visit/patient shape every view here actually reads —
 * deliberately narrow rather than a shared canonical Visit/Patient type,
 * same convention ConsultStage.tsx already established for this codebase. */
export interface MinimalVisit { id: number; visit_id: string }
export interface MinimalPatient { uhid?: string; name?: string }

function InfoCard({ icon, title, children }: { icon?: string; title?: string; children: ReactNode }) {
  return (
    <div className="rounded-[var(--radius)] border border-line bg-card p-3">
      {title && <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-ink-muted">{icon} {title}</div>}
      <div className="flex flex-col gap-2.5">{children}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  if (children === null || children === undefined || children === '') return null;
  return (
    <div>
      <div className="mb-1 text-[11.5px] font-semibold text-ink-muted">{label}</div>
      <div style={{ fontSize: 13, color: 'var(--color-ink)' }}>{children}</div>
    </div>
  );
}

function AdditionalDetails({ data, consumedKeys }: { data: StageData; consumedKeys: string[] }) {
  const leftover = Object.entries(data).filter(([k, v]) => !consumedKeys.includes(k) && !isEmptyValue(v));
  if (!leftover.length) return null;
  return (
    <InfoCard icon="📎" title="Additional details">
      {leftover.map(([key, value]) => (
        <div key={key}>
          <div className="mb-1 text-[11.5px] font-semibold text-ink-muted">{humanize(key)}</div>
          <div style={{ fontSize: 13, color: 'var(--color-ink)' }}><RenderValue value={value} /></div>
        </div>
      ))}
    </InfoCard>
  );
}

function VerifyView({ data }: { data: StageData }) {
  const consumed = ['is_new', 'duplicate_check_ran', 'match_count', 'name', 'mobile', 'dob', 'gender', 'age'];
  return (
    <div className="flex flex-col gap-3.5">
      <InfoCard icon="🔎" title="Identity check">
        <div className="flex flex-wrap gap-2">
          {data.is_new === true && <ValueChip tone="info">🆕 New patient</ValueChip>}
          {data.is_new === false && <ValueChip tone="success">↩ Returning patient</ValueChip>}
          {data.duplicate_check_ran && (
            Number(data.match_count) > 0
              ? <ValueChip tone="warn">⚠ {data.match_count} possible duplicate(s) found</ValueChip>
              : <ValueChip tone="success">✓ No duplicates found</ValueChip>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name">{data.name}</Field>
          <Field label="Mobile">{data.mobile}</Field>
          <Field label="Date of birth">{data.dob}</Field>
          <Field label="Gender">{data.gender}</Field>
          <Field label="Age">{data.age}</Field>
        </div>
      </InfoCard>
      <AdditionalDetails data={data} consumedKeys={consumed} />
    </div>
  );
}

function RegisterView({ data }: { data: StageData }) {
  const consumed = ['uhid', 'patient_id'];
  const rest = Object.entries(data).filter(([k, v]) => !consumed.includes(k) && !isEmptyValue(v));
  return (
    <div className="flex flex-col gap-3.5">
      <InfoCard icon="📋" title="Registration">
        <div className="flex flex-wrap gap-2">
          {data.uhid && <ValueChip tone="info">🪪 UHID {data.uhid}</ValueChip>}
        </div>
        {rest.length > 0 && (
          <div className="grid grid-cols-2 gap-3">
            {rest.map(([k, v]) => <Field key={k} label={humanize(k)}><RenderValue value={v} /></Field>)}
          </div>
        )}
      </InfoCard>
    </div>
  );
}

function VitalsView({ data }: { data: StageData }) {
  const consumed = ['bloodGroup', 'weight', 'height', 'bp_sys', 'bp_dia', 'pulse', 'temp', 'spo2', 'symptoms', 'symptom_form', 'dob', 'age', 'bmi', 'selected_module'];
  const bp = !isEmptyValue(data.bp_sys) && !isEmptyValue(data.bp_dia) ? `${data.bp_sys}/${data.bp_dia}` : null;
  return (
    <div className="flex flex-col gap-3.5">
      <InfoCard icon="🌡️" title="Vitals">
        {/* Ranges from lib/vitalThresholds.ts — the same module Vitals and
            Pre-Consult read, so a reading flagged when it was captured still
            reads flagged here. */}
        <div className="flex flex-wrap gap-2.5">
          <VitalTile icon="🫀" label="BP" value={bp} unit="mmHg" check={checkBp(data.bp_sys as string, data.bp_dia as string)} />
          <VitalTile icon="💓" label="Pulse" value={data.pulse} unit="bpm" check={checkPulse(data.pulse as string)} />
          <VitalTile icon="🌡️" label="Temp" value={data.temp} unit="°F" check={checkTemp(data.temp as string)} />
          <VitalTile icon="🫁" label="SpO₂" value={data.spo2} unit="%" check={checkSpo2(data.spo2 as string)} />
          <StatTile icon="⚖️" label="Weight" value={data.weight} unit="kg" />
          <StatTile icon="📏" label="Height" value={data.height} unit="cm" />
          <VitalTile icon="📊" label="BMI" value={data.bmi} check={checkBmi(data.bmi as string)} />
          <StatTile icon="🩸" label="Blood group" value={data.bloodGroup} />
        </div>
        {data.selected_module && data.selected_module !== 'none' && (
          <ValueChip tone="neutral">📋 Assessment module selected</ValueChip>
        )}
        {readSnapshot(data)
          ? <Field label="Symptoms"><WhyTodaySymptoms vitals={data} fallback="—" /></Field>
          : <Field label="Symptoms / complaints">{data.symptoms}</Field>}
      </InfoCard>
      <AdditionalDetails data={data} consumedKeys={consumed} />
    </div>
  );
}

const PRIORITY_TONE: Record<string, ValueChipTone> = { Normal: 'success', Urgent: 'warn', Emergency: 'danger' };

function QueueView({ data }: { data: StageData }) {
  const consumed = ['queue_id', 'token', 'doctor_id', 'doctor', 'priority'];
  return (
    <div className="flex flex-col gap-3.5">
      <InfoCard icon="🎫" title="Queue assignment">
        <div className="flex flex-wrap items-center gap-2">
          {data.token && <ValueChip tone="info">🎫 Token {data.token}</ValueChip>}
          {data.priority && <ValueChip tone={PRIORITY_TONE[data.priority] || 'neutral'}>{data.priority === 'Emergency' ? '🚨' : data.priority === 'Urgent' ? '⚠' : '✓'} {data.priority}</ValueChip>}
        </div>
        <Field label="Assigned doctor">{data.doctor}</Field>
      </InfoCard>
      <AdditionalDetails data={data} consumedKeys={consumed} />
    </div>
  );
}

// The Consultation stage's own stage_data only ever captures ONE document
// (whichever the doctor approved right before clicking Continue) — it was
// never meant to be the full clinical record of the visit. Allergies,
// diagnoses and the prescription are each their own structured table (see
// [ABDM / FHIR Interoperability Programme] in CLAUDE.md) written live during
// this same stage by AllergyPanel/DiagnosisPanel/PrescriptionBuilder, so a
// read-only view that shows only stage_data.content is missing most of what
// the doctor actually captured. This fetches all three, the same way
// ConsultStage.tsx itself does, so "what happened at consultation" reads
// complete here too.
const ALLERGY_TONE: Record<string, ValueChipTone> = { high: 'danger', low: 'warn', 'unable-to-assess': 'neutral' };

function ConsultationClinicalCapture({ uhid, visitCode, visitId }: { uhid: string; visitCode: string; visitId: number }) {
  const [allergies, setAllergies] = useState<Allergy[] | null>(null);
  const [diagnoses, setDiagnoses] = useState<EncounterDiagnosis[] | null>(null);
  const [prescription, setPrescription] = useState<Prescription | false | null>(null);

  useEffect(() => {
    if (uhid) listAllergies(uhid).then(setAllergies).catch(() => setAllergies([]));
  }, [uhid]);
  useEffect(() => {
    if (visitCode) listVisitDiagnoses(visitCode).then(setDiagnoses).catch(() => setDiagnoses([]));
  }, [visitCode]);
  useEffect(() => {
    if (!visitId) return;
    listPrescriptionsForVisit(visitId)
      .then(list => {
        const rx = list?.[0];
        if (!rx) { setPrescription(false); return; }
        getPrescriptionVersioned(rx.id).then(({ data: full }) => setPrescription(full)).catch(() => setPrescription(rx));
      })
      .catch(() => setPrescription(false));
  }, [visitId]);

  const activeAllergies = (allergies || []).filter(a => a.clinical_status === 'active');

  return (
    <>
      {activeAllergies.length > 0 && (
        <InfoCard icon="⚠" title="Allergies">
          <div className="flex flex-wrap gap-2">
            {activeAllergies.map(a => (
              <ValueChip key={a.id} tone={(a.criticality && ALLERGY_TONE[a.criticality]) || 'warn'}>
                {a.substance}{a.reaction ? ` — ${a.reaction}` : ''}
              </ValueChip>
            ))}
          </div>
        </InfoCard>
      )}

      {diagnoses && diagnoses.length > 0 && (
        <InfoCard icon="🩺" title="Diagnoses (this visit)">
          <div className="flex flex-wrap gap-2">
            {diagnoses.map(d => (
              <ValueChip key={d.id} tone={d.verification_status === 'confirmed' ? 'info' : 'neutral'}>
                {d.diagnosis}{d.rank === 1 ? ' (primary)' : ''}
              </ValueChip>
            ))}
          </div>
        </InfoCard>
      )}

      {prescription && (
        <InfoCard icon="💊" title="Prescription">
          <div className="flex flex-wrap gap-2">
            <ValueChip tone={prescription.status === 'active' ? 'success' : prescription.status === 'draft' ? 'neutral' : 'warn'}>
              {prescription.status}
            </ValueChip>
          </div>
          <div className="flex flex-col gap-2">
            {(prescription.items || []).map(item => (
              <div key={item.id} className="rounded-[var(--radius)] border border-line bg-canvas/60 px-3 py-2" style={{ fontSize: 12.5 }}>
                <b>{item.brand_name || item.drug_name}</b>{item.strength ? ` ${item.strength}` : ''}
                {item.brand_name && item.brand_name !== item.drug_name && <span style={{ color: 'var(--color-ink-muted)' }}> ({item.drug_name})</span>}
                <div style={{ color: 'var(--color-ink-muted)' }}>
                  {[item.dose_quantity && `${Number(item.dose_quantity)}${item.dose_unit || ''}`, item.route, item.frequency_text, item.duration_value && `${Number(item.duration_value)}${item.duration_unit}`]
                    .filter(Boolean).join(' · ') || '—'}
                </div>
              </div>
            ))}
          </div>
        </InfoCard>
      )}
    </>
  );
}

function ConsultationView({ data, visit, patient }: { data: StageData; visit?: MinimalVisit; patient?: MinimalPatient }) {
  const consumed = ['doc_type', 'content', 'provider', 'model', 'consultation_id', 'document_id', 'document_uhid', 'approved', 'visit_type'];
  return (
    <div className="flex flex-col gap-3.5">
      <InfoCard icon="🩺" title="Consultation">
        <div className="flex flex-wrap gap-2">
          {data.visit_type && <ValueChip tone={data.visit_type === 'IP' ? 'caution' : 'info'}>{data.visit_type === 'IP' ? '🛏 Admit to IP' : '🚶 OP — discharge today'}</ValueChip>}
          {data.doc_type && <ValueChip tone="neutral">📄 {data.doc_type}</ValueChip>}
        </div>
        {/* Same DocumentEditor the doctor approved this note in during
            Consultation (ConsultStage.tsx) — formatted (bold labels, smart
            fields rendered as plain filled text once approved) rather than
            RenderValue's generic value dump, so a stage this preview reopens
            reads exactly as it did when it was written. Read-only: this is a
            historical record, not a place to re-edit it. */}
        {data.content && (
          <DocumentEditor value={data.content} onChange={() => {}} readOnly ariaLabel="Consultation note" />
        )}
        {!data.content && (
          <div style={{ fontSize: 12.5, color: 'var(--color-ink-muted)' }}>No document text was captured on this stage record.</div>
        )}
        {data.document_id && data.document_uhid && (
          <DocumentPreviewButtons uhid={data.document_uhid} docId={data.document_id} label={data.doc_type || 'Consultation document'} />
        )}
      </InfoCard>

      {visit && patient?.uhid && (
        <ConsultationClinicalCapture uhid={patient.uhid} visitCode={visit.visit_id} visitId={visit.id} />
      )}

      <AdditionalDetails data={data} consumedKeys={consumed} />
    </div>
  );
}

function IPView({ data }: { data: StageData }) {
  const consumed = ['visit_type', 'admission_id', 'bed_id'];
  return (
    <div className="flex flex-col gap-3.5">
      <InfoCard icon="🛏" title="IP admission">
        <div className="flex flex-wrap gap-2">
          <ValueChip tone="caution">🛏 Admitted</ValueChip>
          {data.bed_id && <ValueChip tone="neutral">Bed {data.bed_id}</ValueChip>}
        </div>
        <Field label="Admission ID">{data.admission_id}</Field>
      </InfoCard>
      <AdditionalDetails data={data} consumedKeys={consumed} />
    </div>
  );
}

const DISPENSE_REASON_LABEL: Record<string, string> = {
  patient_declined: 'Patient declined',
  patient_will_collect_later: 'Patient will collect later',
  patient_purchased_elsewhere: 'Patient purchased elsewhere',
  doctor_cancelled: 'Doctor cancelled prescription',
  insurance_payment_issue: 'Insurance/payment issue',
  nothing_to_dispense: 'Nothing to dispense',
  other: 'Other',
};

function money(n: unknown): string | null {
  return isEmptyValue(n) ? null : `₹${Number(n).toLocaleString('en-IN')}`;
}

function PharmacyView({ data }: { data: StageData }) {
  const consumed = ['dispensed', 'bill_id', 'bill_number', 'total_amount', 'dispense_reason', 'dispense_reason_text'];
  return (
    <div className="flex flex-col gap-3.5">
      <InfoCard icon="💊" title="Pharmacy">
        <div className="flex flex-wrap gap-2">
          {data.dispensed
            ? <ValueChip tone="success">✓ Dispensed</ValueChip>
            : <ValueChip tone="neutral">⊘ Not dispensed{data.dispense_reason ? ` — ${DISPENSE_REASON_LABEL[data.dispense_reason] || humanize(data.dispense_reason)}` : ''}</ValueChip>}
          {data.bill_number && <ValueChip tone="info">🧾 {data.bill_number}</ValueChip>}
          {money(data.total_amount) && <ValueChip tone="info">{money(data.total_amount)}</ValueChip>}
        </div>
        <Field label="Reason">{data.dispense_reason_text}</Field>
      </InfoCard>
      <AdditionalDetails data={data} consumedKeys={consumed} />
    </div>
  );
}

function BillingView({ data }: { data: StageData }) {
  const consumed = ['bill_id', 'bill_number', 'total_amount'];
  return (
    <div className="flex flex-col gap-3.5">
      <InfoCard icon="🧾" title="Billing">
        <div className="flex flex-wrap gap-2">
          {data.bill_number && <ValueChip tone="success">🧾 {data.bill_number}</ValueChip>}
          {money(data.total_amount) && <ValueChip tone="info">{money(data.total_amount)}</ValueChip>}
        </div>
      </InfoCard>
      <AdditionalDetails data={data} consumedKeys={consumed} />
    </div>
  );
}

const RESOLUTION_META: Record<string, { label: string; icon: string; tone: ValueChipTone }> = {
  closed: { label: 'Case closed', icon: '✅', tone: 'success' },
  referred: { label: 'Referred', icon: '↗', tone: 'warn' },
  admitted: { label: 'Admitted (IP)', icon: '🛏', tone: 'info' },
  followup: { label: 'Follow-up', icon: '📅', tone: 'caution' },
};

function ResolveView({ data }: { data: StageData }) {
  const consumed = ['resolution', 'resolution_notes', 'followup_date'];
  const meta = RESOLUTION_META[data.resolution];
  return (
    <div className="flex flex-col gap-3.5">
      <InfoCard icon="🏁" title="Resolution">
        <div className="flex flex-wrap gap-2">
          {meta && <ValueChip tone={meta.tone}>{meta.icon} {meta.label}</ValueChip>}
          {data.followup_date && <ValueChip tone="neutral">📅 {new Date(data.followup_date).toLocaleDateString('en-IN')}</ValueChip>}
        </div>
        <Field label="Notes">{data.resolution_notes}</Field>
      </InfoCard>
      <AdditionalDetails data={data} consumedKeys={consumed} />
    </div>
  );
}

interface StageViewProps { data: StageData; visit?: MinimalVisit; patient?: MinimalPatient }

const VIEWS: Record<string, (props: StageViewProps) => ReactNode> = {
  verify: VerifyView,
  register: RegisterView,
  vitals: VitalsView,
  queue: QueueView,
  consultation: ConsultationView,
  ip: IPView,
  pharmacy: PharmacyView,
  billing: BillingView,
  resolve: ResolveView,
};

export default function StageSummaryView({ stageKey, data, visit, patient }: { stageKey: string } & StageViewProps) {
  const View = VIEWS[stageKey];
  if (!View) return null;
  return <>{View({ data, visit, patient })}</>;
}
